import { RATING_CONFIG, rateSurfQuality, type SurfRating } from "@/lib/conditions";
import { getSeoPageByPath } from "@/lib/seoPages";
import { getSpotPageByPath, type SpotPageConfig } from "@/lib/spotPages";

const METERS_TO_FEET = 3.28084;
const KMH_TO_MPH = 0.621371;
const COMPASS_8 = ["N", "NE", "E", "SE", "S", "SW", "W", "NW"] as const;

export const HOME_TITLE =
  "howisthe.surf — Live Surf Reports, Cams & Forecasts (Portugal & Florida)";
export const HOME_DESCRIPTION =
  "Live surf cams, multi-model wave forecasts, wind, tide, and community reports for Flagler Beach, Northeast Florida, Algarve, Peniche, and more.";
export const HOME_H1 = "How is the surf?";
export const HOME_LEAD =
  "Check live cameras and ensemble swell, wind, and tide for any coast we cover — starting with Flagler Beach Pier and Portugal’s Costa Oeste / Algarve. Pick a region below.";

export const HOME_LINKS = [
  { href: "/florida-surf-reports", label: "Florida surf reports" },
  { href: "/portugal-surf-reports", label: "Portugal surf reports" },
  { href: "/flagler-beach-surf-report", label: "Flagler Beach surf report" },
  { href: "/flagler-beach-surf-cam", label: "Flagler Beach surf cam" },
  { href: "/flagler-beach-tide", label: "Flagler Beach tide" },
  { href: "/flagler-beach-water-temperature", label: "Flagler Beach water temperature" },
  { href: "/northeast-florida-surf-report", label: "Northeast Florida" },
  { href: "/algarve-surf-report", label: "Algarve" },
] as const;

export const LLMS_TXT = `# howisthe.surf
> Live surf cams, multi-model forecasts, wind, tide, buoys, and community reports.

Canonical: https://www.howisthe.surf/

## Primary hubs
- https://www.howisthe.surf/florida-surf-reports
- https://www.howisthe.surf/northeast-florida-surf-report
- https://www.howisthe.surf/flagler-beach-surf-report
- https://www.howisthe.surf/flagler-beach-surf-cam
- https://www.howisthe.surf/flagler-beach-tide
- https://www.howisthe.surf/flagler-beach-water-temperature
- https://www.howisthe.surf/portugal-surf-reports
- https://www.howisthe.surf/algarve-surf-report
- https://www.howisthe.surf/peniche-nazare-surf-report
- https://www.howisthe.surf/surf-spots

## Notes
- Forecasts update through the day; cite the on-page timestamp.
- Prefer www. Soft-404s should not exist for unknown paths.

## Rank checks
- flagler beach surf cam
- flagler beach surf report
- flagler beach tide
- flagler beach water temperature
- how is the surf
`;

export type AnswerUnits = "us" | "metric";
export type FaqPageId = "flagler" | "arrifana" | "northeast-florida" | "algarve";

export interface AnswerPageConfig {
  path: string;
  /** "How is the surf at/in {subject}" */
  subject: string;
  /** Spot named inside hub answers. Same as subject on spot pages. */
  placeName: string;
  voice: "spot" | "reference";
  units: AnswerUnits;
  regionId: string;
  breakId: string;
  closing: string;
  /** Shown when swell, wind, and a timestamp are not all available. */
  staticDetail: string;
  faqId?: FaqPageId;
}

export interface AnswerFacts {
  ratingLabel?: string;
  /** True when height is a surf-face estimate distinct from the swell figure. */
  heightIsFace: boolean;
  heightFt: number;
  heightM: number;
  windMph: number;
  windKmh: number;
  windDir: string;
  swellFt: number;
  swellM: number;
  swellPeriodS: number;
  swellDir: string;
  timestampLocal: string;
}

export interface FaqEntry {
  question: string;
  answer: string;
}

export function compass8(degrees: number | null | undefined): string | null {
  if (degrees == null || !Number.isFinite(degrees)) return null;
  const normalized = ((degrees % 360) + 360) % 360;
  const index = Math.round(normalized / 45) % 8;
  return COMPASS_8[index];
}

export function buildWebsiteJsonLd() {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: "howisthe.surf",
    url: "https://www.howisthe.surf/",
    description:
      "Live surf cams, ensemble forecasts, wind, tide, and community reports for Portugal and Florida.",
  };
}

export function buildFaqJsonLd(items: FaqEntry[]) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: items.map((item) => ({
      "@type": "Question",
      name: item.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: item.answer,
      },
    })),
  };
}

export function formatLocalTimestamp(
  iso: string | null | undefined,
  timeZone: string | null | undefined,
): string | null {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  const zone = timeZone && isValidTimeZone(timeZone) ? timeZone : "UTC";
  return new Intl.DateTimeFormat("en-US", {
    timeZone: zone,
    dateStyle: "medium",
    timeStyle: "short",
  }).format(date);
}

function isValidTimeZone(timeZone: string): boolean {
  try {
    new Intl.DateTimeFormat("en-US", { timeZone }).format(new Date());
    return true;
  } catch {
    return false;
  }
}

function finite(value: number | null | undefined): value is number {
  return typeof value === "number" && Number.isFinite(value);
}

function round1(value: number): number {
  return Math.round(value * 10) / 10;
}

function oneDecimal(value: number): string {
  return round1(value).toFixed(1);
}

export function factsFromPublishedForecast(input: {
  swellFt?: number | null;
  swellPeriodS?: number | null;
  swellDirection?: string | null;
  windMph?: number | null;
  windDirection?: string | null;
  generatedAt?: string | null;
  timeZone?: string | null;
}): AnswerFacts | null {
  if (
    !finite(input.swellFt) ||
    !finite(input.swellPeriodS) ||
    !input.swellDirection ||
    !finite(input.windMph) ||
    !input.windDirection
  ) {
    return null;
  }
  const timestampLocal = formatLocalTimestamp(input.generatedAt, input.timeZone);
  if (!timestampLocal) return null;
  const swellM = input.swellFt / METERS_TO_FEET;
  return {
    heightIsFace: false,
    heightFt: input.swellFt,
    heightM: swellM,
    windMph: input.windMph,
    windKmh: input.windMph / KMH_TO_MPH,
    windDir: input.windDirection,
    swellFt: input.swellFt,
    swellM,
    swellPeriodS: input.swellPeriodS,
    swellDir: input.swellDirection,
    timestampLocal,
  };
}

export function factsFromReadings(input: {
  rating?: SurfRating | null;
  faceHeightM?: number | null;
  swellHeightM?: number | null;
  swellPeriodS?: number | null;
  swellDirectionDeg?: number | null;
  windSpeedKmh?: number | null;
  windDirectionDeg?: number | null;
  updatedAtIso?: string | null;
  timeZone?: string | null;
}): AnswerFacts | null {
  const swellM = finite(input.swellHeightM) ? input.swellHeightM : input.faceHeightM;
  if (!finite(input.faceHeightM) || !finite(swellM) || !finite(input.swellPeriodS)) {
    return null;
  }
  if (!finite(input.windSpeedKmh)) return null;
  const swellDir = compass8(input.swellDirectionDeg);
  const windDir = compass8(input.windDirectionDeg);
  if (!swellDir || !windDir) return null;
  const timestampLocal = formatLocalTimestamp(input.updatedAtIso, input.timeZone);
  if (!timestampLocal) return null;
  const heightFt = round1(input.faceHeightM * METERS_TO_FEET);
  const heightM = round1(input.faceHeightM);
  const publishedSwellFt = round1(swellM * METERS_TO_FEET);
  const publishedSwellM = round1(swellM);
  return {
    ratingLabel: input.rating ? RATING_CONFIG[input.rating].label : undefined,
    heightIsFace: Math.abs(heightFt - publishedSwellFt) >= 0.15,
    heightFt,
    heightM,
    windMph: input.windSpeedKmh * KMH_TO_MPH,
    windKmh: input.windSpeedKmh,
    windDir,
    swellFt: publishedSwellFt,
    swellM: publishedSwellM,
    swellPeriodS: input.swellPeriodS,
    swellDir,
    timestampLocal,
  };
}

export function rateAnswer(input: {
  faceHeightM: number;
  periodS: number | null;
  windSpeedKmh: number | null;
  windGustKmh: number | null;
  windDirectionDeg: number | null;
  swellDirectionDeg: number | null;
  shoreNormal: number | null;
  tideQualityFactor?: number | null;
}): SurfRating {
  return rateSurfQuality({
    waveHeightFt: input.faceHeightM * METERS_TO_FEET,
    period: input.periodS,
    windSpeedKmh: input.windSpeedKmh,
    windGustKmh: input.windGustKmh,
    windDirectionDeg: input.windDirectionDeg,
    swellDirectionDeg: input.swellDirectionDeg,
    shoreNormal: input.shoreNormal,
    tideQualityFactor: input.tideQualityFactor,
  }).rating;
}

function windClause(facts: AnswerFacts, units: AnswerUnits): string {
  if (units === "us") return `${Math.round(facts.windMph)} mph ${facts.windDir} wind`;
  return `${Math.round(facts.windKmh)} km/h ${facts.windDir} wind`;
}

function conditionsClause(facts: AnswerFacts, units: AnswerUnits): string {
  const period = Math.round(facts.swellPeriodS);
  if (!facts.heightIsFace) {
    const swell =
      units === "us"
        ? `${oneDecimal(facts.swellFt)} ft (${oneDecimal(facts.swellM)} m)`
        : `${oneDecimal(facts.swellM)} m (${oneDecimal(facts.swellFt)} ft)`;
    const lead = facts.ratingLabel
      ? `${facts.ratingLabel} — published swell`
      : "Published swell";
    return `${lead} ${swell} @ ${period}s ${facts.swellDir}, with ${windClause(facts, units)}`;
  }
  const height =
    units === "us"
      ? `about ${oneDecimal(facts.heightFt)} ft (${oneDecimal(facts.heightM)} m)`
      : `about ${oneDecimal(facts.heightM)} m (${oneDecimal(facts.heightFt)} ft)`;
  const swell =
    units === "us"
      ? `primary swell ${oneDecimal(facts.swellFt)} ft @ ${period}s ${facts.swellDir}`
      : `primary swell ${oneDecimal(facts.swellM)} m @ ${period}s ${facts.swellDir}`;
  const rating = facts.ratingLabel ? `${facts.ratingLabel} — ` : "";
  return `${rating}${height} with ${windClause(facts, units)} and ${swell}`;
}

export function formatConditionsSentence(
  page: AnswerPageConfig,
  facts: AnswerFacts | null,
): string {
  if (!facts) {
    const where = page.voice === "reference" ? "in" : "at";
    return `How is the surf ${where} ${page.subject} right now? ${page.staticDetail}`;
  }
  const clause = conditionsClause(facts, page.units);
  const updated = `Updated ${facts.timestampLocal}. ${page.closing}`;
  if (page.voice === "reference") {
    return `How is the surf in ${page.subject} right now? At ${page.placeName}, ${clause}. ${updated}`;
  }
  return `How is the surf at ${page.subject} right now? ${clause}. ${updated}`;
}

function flaglerToday(facts: AnswerFacts | null): string {
  if (!facts) {
    return "The Flagler Beach Pier page on howisthe.surf shows the live cam, wave height in feet, swell, wind, and tide, along with the time of the forecast.";
  }
  const period = Math.round(facts.swellPeriodS);
  const wind = `${Math.round(facts.windMph)} mph ${facts.windDir}`;
  const swell = `${oneDecimal(facts.swellFt)} ft @ ${period}s ${facts.swellDir}`;
  if (facts.ratingLabel && facts.heightIsFace) {
    return `As of ${facts.timestampLocal}, Flagler Beach Pier is ${facts.ratingLabel} at about ${oneDecimal(facts.heightFt)} ft, with ${wind} wind and ${swell} swell. Check the live pier cam on howisthe.surf before you go.`;
  }
  return `As of ${facts.timestampLocal}, the published forecast for Flagler Beach Pier is ${swell} swell with ${wind} wind. Check the live pier cam on howisthe.surf before you go.`;
}

function arrifanaToday(facts: AnswerFacts | null): string {
  if (!facts) {
    return "The Arrifana page on howisthe.surf shows the live cam, wave height, swell, wind, and tide, along with the time of the forecast.";
  }
  const period = Math.round(facts.swellPeriodS);
  const wind = `${Math.round(facts.windKmh)} km/h ${facts.windDir}`;
  const swell = `${oneDecimal(facts.swellM)} m @ ${period}s ${facts.swellDir}`;
  if (facts.ratingLabel && facts.heightIsFace) {
    return `As of ${facts.timestampLocal}, Arrifana is ${facts.ratingLabel} at about ${oneDecimal(facts.heightM)} m (${oneDecimal(facts.heightFt)} ft), with ${wind} wind and ${swell} swell. Check the live cam on howisthe.surf before you go.`;
  }
  return `As of ${facts.timestampLocal}, the published forecast for Arrifana is ${swell} swell with ${wind} wind. Check the live cam on howisthe.surf before you go.`;
}

function northeastFloridaToday(facts: AnswerFacts | null): string {
  if (!facts) {
    return "Compare Flagler Beach Pier, St Augustine, and Jax Beach on howisthe.surf. Flagler is the live-cam check, and each report shows swell, wind, tide, and the forecast time. Wave heights are in feet.";
  }
  const period = Math.round(facts.swellPeriodS);
  const wind = `${Math.round(facts.windMph)} mph ${facts.windDir}`;
  const swell = `${oneDecimal(facts.swellFt)} ft @ ${period}s ${facts.swellDir}`;
  if (facts.ratingLabel && facts.heightIsFace) {
    return `As of ${facts.timestampLocal}, Flagler Beach Pier — the live-cam check for Northeast Florida — is ${facts.ratingLabel} at about ${oneDecimal(facts.heightFt)} ft, with ${wind} wind and ${swell} swell. Compare St Augustine and Jax Beach before you drive.`;
  }
  return `As of ${facts.timestampLocal}, the published forecast at Flagler Beach Pier is ${swell} swell with ${wind} wind. Compare St Augustine and Jax Beach before you drive.`;
}

function algarveToday(facts: AnswerFacts | null): string {
  if (!facts) {
    return "Compare Arrifana, Monte Clérigo, Praia do Amado, and Praia da Luz. West-coast and south-coast beaches can differ on the same tide. Each page shows the cam or report, swell, wind, and the forecast time.";
  }
  const period = Math.round(facts.swellPeriodS);
  const wind = `${Math.round(facts.windKmh)} km/h ${facts.windDir}`;
  const swell = `${oneDecimal(facts.swellM)} m @ ${period}s ${facts.swellDir}`;
  if (facts.ratingLabel && facts.heightIsFace) {
    return `As of ${facts.timestampLocal}, Arrifana is ${facts.ratingLabel} at about ${oneDecimal(facts.heightM)} m (${oneDecimal(facts.heightFt)} ft), with ${wind} wind and ${swell} swell. West-coast and south-coast breaks can differ — compare Monte Clérigo, Amado, and Praia da Luz.`;
  }
  return `As of ${facts.timestampLocal}, the published forecast at Arrifana is ${swell} swell with ${wind} wind. West-coast and south-coast breaks can differ — compare Monte Clérigo, Amado, and Praia da Luz.`;
}

const FLAGLER_REST: FaqEntry[] = [
  {
    question: "What swell and wind work best at Flagler Beach Pier?",
    answer:
      "Flagler Beach Pier is an exposed Northeast Florida beach break that responds quickly to short-period wind swell, tide windows, and local wind shifts. Cleaner faces usually need lighter offshore or side-offshore winds; strong onshore NE/NNE winds get choppy fast.",
  },
  {
    question: "Does howisthe.surf have a Flagler Beach live cam?",
    answer:
      "Yes. The Flagler Beach surf cam page links the FlaglerSurf camera, and the pier report plays the live pier stream with forecast, tide, wind, and water temperature.",
  },
];

const ARRIFANA_REST: FaqEntry[] = [
  {
    question: "What swell and wind work best at Arrifana?",
    answer:
      "Arrifana is a sheltered Algarve west-coast beach that can stay cleaner than nearby open beaches when the wind picks up. It still needs a west or northwest swell to wrap into the bay, and a tide that isn't draining it flat. Compare Monte Clérigo and Amado when the exposed coast looks better.",
  },
  {
    question: "Does howisthe.surf have an Arrifana live cam?",
    answer:
      "Yes. The Arrifana page on howisthe.surf includes the live cam with multi-model forecast, tide, wind, and buoy context.",
  },
];

const NORTHEAST_FLORIDA_REST: FaqEntry[] = [
  {
    question: "Which Northeast Florida spot should I check first?",
    answer:
      "Start with Flagler Beach Pier for the live cam, then compare St Augustine Pier and Jax Beach. Wind, tide, and swell angle can differ by county on this stretch of coast.",
  },
  {
    question: "Does Northeast Florida have a live surf cam on howisthe.surf?",
    answer:
      "Yes. Flagler Beach Pier is the live camera for Northeast Florida, with forecast, tide, wind, and buoy context on the same page.",
  },
];

const ALGARVE_REST: FaqEntry[] = [
  {
    question: "Which Algarve coast should I check when it's windy?",
    answer:
      "The west coast — Arrifana, Monte Clérigo, and Amado — is the more exposed side. South-coast beaches such as Praia da Luz, Zavial, and Mareta are the usual comparison when a northwest wind or a large west swell makes the west coast messy.",
  },
  {
    question: "Does howisthe.surf have Algarve live cams?",
    answer:
      "Yes. Arrifana, Monte Clérigo, Amado, Praia da Luz, and other Algarve breaks have report pages, and the Algarve multicam view compares several of them at once.",
  },
];

export function buildFaqs(
  page: AnswerPageConfig,
  facts: AnswerFacts | null,
): FaqEntry[] | null {
  if (page.faqId === "flagler") {
    return [
      {
        question: "How is the surf at Flagler Beach today?",
        answer: flaglerToday(facts),
      },
      ...FLAGLER_REST,
    ];
  }
  if (page.faqId === "arrifana") {
    return [
      { question: "How is the surf at Arrifana today?", answer: arrifanaToday(facts) },
      ...ARRIFANA_REST,
    ];
  }
  if (page.faqId === "northeast-florida") {
    return [
      {
        question: "How is the surf in Northeast Florida today?",
        answer: northeastFloridaToday(facts),
      },
      ...NORTHEAST_FLORIDA_REST,
    ];
  }
  if (page.faqId === "algarve") {
    return [
      { question: "How is the surf in the Algarve today?", answer: algarveToday(facts) },
      ...ALGARVE_REST,
    ];
  }
  return null;
}

const FAQ_PAGES: Record<string, Omit<AnswerPageConfig, "path">> = {
  "/flagler-beach-surf-report": {
    subject: "Flagler Beach",
    placeName: "Flagler Beach Pier",
    voice: "spot",
    units: "us",
    regionId: "Northeast Florida",
    breakId: "flagler-beach-pier",
    closing: "Use the pier cam plus tide before you paddle out.",
    staticDetail:
      "Wave heights here are reported in feet. The pier cam, tide, and forecast readout on this page are the check before you paddle out.",
    faqId: "flagler",
  },
  "/arrifana-surf-report": {
    subject: "Arrifana",
    placeName: "Arrifana",
    voice: "spot",
    units: "metric",
    regionId: "Praias Algarve",
    breakId: "arrifana",
    closing: "Check the live cam and tide before you paddle out.",
    staticDetail:
      "The live cam, tide, and forecast readout on this page are the check before you paddle out.",
    faqId: "arrifana",
  },
  "/northeast-florida-surf-report": {
    subject: "Northeast Florida",
    placeName: "Flagler Beach Pier",
    voice: "reference",
    units: "us",
    regionId: "Northeast Florida",
    breakId: "flagler-beach-pier",
    closing: "Compare the pier cam with St Augustine and Jax Beach before you drive.",
    staticDetail:
      "Wave heights on this coast are reported in feet. Flagler Beach Pier is the live-cam check — compare it with St Augustine and Jax Beach before you drive.",
    faqId: "northeast-florida",
  },
  "/algarve-surf-report": {
    subject: "the Algarve",
    placeName: "Arrifana",
    voice: "reference",
    units: "metric",
    regionId: "Praias Algarve",
    breakId: "arrifana",
    closing: "Compare Arrifana with Monte Clérigo and Amado before you choose a beach.",
    staticDetail:
      "Arrifana is the usual first check. West-coast and south-coast beaches can differ, so compare Monte Clérigo, Amado, and Praia da Luz before you choose.",
    faqId: "algarve",
  },
};

function spotUnits(page: SpotPageConfig): AnswerUnits {
  return page.state === "FL" ? "us" : "metric";
}

export function answerConfigForPath(pathname: string): AnswerPageConfig | null {
  const path = pathname.startsWith("/") ? pathname : `/${pathname}`;
  const special = FAQ_PAGES[path];
  if (special) return { path, ...special };

  const spot = getSpotPageByPath(path);
  if (!spot) return null;
  const units = spotUnits(spot);
  return {
    path: spot.path,
    subject: spot.spotName,
    placeName: spot.displayName,
    voice: "spot",
    units,
    regionId: spot.regionId,
    breakId: spot.cameraId,
    closing: "Check the live cam, tide, and forecast on this page before you paddle out.",
    staticDetail:
      units === "us"
        ? "Wave heights here are reported in feet. The cam, tide, and forecast readout on this page are the check before you paddle out."
        : "The live cam, tide, and forecast readout on this page are the check before you paddle out.",
  };
}

export function answerConfigForHubPath(pathname: string): AnswerPageConfig | null {
  const path = pathname.startsWith("/") ? pathname : `/${pathname}`;
  if (!getSeoPageByPath(path)) return null;
  return FAQ_PAGES[path] ? { path, ...FAQ_PAGES[path] } : null;
}

export interface PrerenderInput {
  introTitle: string;
  introBody: string;
  sentence: string | null;
  locationLine: string | null;
  about: string | null;
  regionIds: string[] | null;
  related: { href: string; label: string }[];
  faqs: FaqEntry[] | null;
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/"/g, "&quot;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

/** Visible article placed inside `#root`. No clipping, no aria-hidden. */
export function renderPrerenderArticle(input: PrerenderInput): string {
  const parts: string[] = [];
  parts.push(`<h1>${escapeHtml(input.introTitle)}</h1>`);
  if (input.sentence) parts.push(`<p>${escapeHtml(input.sentence)}</p>`);
  if (input.introBody) parts.push(`<p>${escapeHtml(input.introBody)}</p>`);
  if (input.locationLine) parts.push(`<p>${escapeHtml(input.locationLine)}</p>`);
  if (input.about) {
    parts.push("<h2>About this report</h2>");
    parts.push(`<p>${escapeHtml(input.about)}</p>`);
  }
  if (input.regionIds && input.regionIds.length > 0) {
    parts.push("<h2>Regions covered</h2>");
    parts.push(
      `<ul>${input.regionIds.map((region) => `<li>${escapeHtml(region)}</li>`).join("")}</ul>`,
    );
  }
  if (input.related.length > 0) {
    parts.push("<h2>Related surf reports</h2>");
    parts.push(
      `<ul>${input.related
        .map(
          (link) =>
            `<li><a href="${escapeHtml(link.href)}">${escapeHtml(link.label)}</a></li>`,
        )
        .join("")}</ul>`,
    );
  }
  if (input.faqs && input.faqs.length > 0) {
    parts.push("<h2>Common questions</h2>");
    for (const faq of input.faqs) {
      parts.push(`<h3>${escapeHtml(faq.question)}</h3>`);
      parts.push(`<p>${escapeHtml(faq.answer)}</p>`);
    }
  }
  return `<article id="seo-content">${parts.join("")}</article>`;
}

export function renderHomeArticle(): string {
  const links = HOME_LINKS.map(
    (link) => `<li><a href="${link.href}">${escapeHtml(link.label)}</a></li>`,
  ).join("");
  return `<article id="seo-content"><h1>${escapeHtml(HOME_H1)}</h1><p>${escapeHtml(HOME_LEAD)}</p><ul>${links}</ul></article>`;
}

export function aboutReport(units: AnswerUnits, displayName: string): string {
  const unitsNote =
    units === "us"
      ? " Wave heights in the answer line are in feet, with meters in parentheses."
      : "";
  return `The ${displayName} surf report on howisthe.surf combines live cam footage, a multi-model wave forecast (ECMWF WAM, NCEP GFS WaveWatch III, and MeteoFrance MFWAM), wind and tide data, and the latest in-range buoy observation. The forecast is bias-corrected against recent paired observations so the wave height shown reflects measured accuracy at this region rather than a raw model average.${unitsNote}`;
}
